import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, Plus, ArrowLeft, Link2, FileDown, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card, SaveButton } from "../ui";
import TripForm from "@/components/planner/TripForm";
import EstimateResult from "@/components/planner/EstimateResult";

const money = (n, c = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n || 0);
const IN = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";

const TABS = [
  ["quote", "New quote"],
  ["quotes", "Saved quotes"],
  ["pricing", "Pricing settings"],
  ["addons", "Add-ons"],
  ["links", "Client links"],
];

export default function RelocationPlanner() {
  const [tab, setTab] = useState("quote");
  const [addons, setAddons] = useState([]);

  useEffect(() => { api.get("/admin/planner/addons").then((r) => setAddons(r.data)); }, []);

  return (
    <div data-testid="admin-planner">
      <SectionTitle title="Route Planner & Pricing" subtitle="Plan the safest route, build a fair modular quote, and share a private planner link with clients." />
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`planner-tab-${id}`} onClick={() => setTab(id)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-terracotta text-white" : "bg-white border border-sand-200 text-charcoal-600 hover:bg-sand-50"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "quote" && <NewQuote addons={addons} />}
      {tab === "quotes" && <SavedQuotes />}
      {tab === "pricing" && <PricingSettings />}
      {tab === "addons" && <AddonsManager onChange={() => api.get("/admin/planner/addons").then((r) => setAddons(r.data))} />}
      {tab === "links" && <ClientLinks />}
    </div>
  );
}

/* ---------------- New quote / estimate ---------------- */
function NewQuote({ addons }) {
  const [trip, setTrip] = useState({ toll_preference: "balanced", stops: [], addons: [], pet_count: 1 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setTrip((t) => ({ ...t, [k]: v }));

  const estimate = async () => {
    if (!trip.start || !trip.end) return toast.error("Enter a start and destination.");
    setBusy(true);
    try { setResult((await api.post("/admin/planner/estimate", trip)).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const saveQuote = async () => {
    try { await api.post("/admin/planner/quotes", trip); toast.success("Quote saved to Saved quotes."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <TripForm trip={trip} set={set} addons={addons.filter((a) => a.enabled)} showClientFields />
        <div className="mt-5 flex gap-3">
          <button data-testid="planner-estimate-btn" onClick={estimate} disabled={busy}
            className="rounded-full bg-charcoal-800 px-6 py-2.5 text-sm text-white font-medium hover:bg-charcoal-900 disabled:opacity-60">
            {busy ? "Calculating…" : "Calculate estimate"}
          </button>
          {result && (
            <button data-testid="planner-save-quote-btn" onClick={saveQuote}
              className="rounded-full border border-sand-300 px-6 py-2.5 text-sm text-charcoal-700 font-medium hover:bg-sand-50">
              Save as quote
            </button>
          )}
        </div>
      </Card>
      {result && <EstimateResult route={result.route} pricing={result.pricing} />}
    </div>
  );
}

/* ---------------- Saved quotes + admin override ---------------- */
const STATUSES = ["draft", "submitted", "reviewed", "sent", "accepted", "declined"];

function SavedQuotes() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/admin/planner/quotes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  if (active) return <QuoteDetail id={active} onBack={() => { setActive(null); load(); }} />;
  if (!rows) return <p className="text-charcoal-500">Loading…</p>;
  if (!rows.length) return <Card><p className="text-charcoal-500">No quotes yet. Create one from “New quote”.</p></Card>;

  return (
    <div className="space-y-3">
      {rows.map((q) => (
        <button key={q.id} data-testid={`quote-row-${q.id}`} onClick={() => setActive(q.id)}
          className="w-full text-left rounded-xl border border-sand-200 bg-white p-4 hover:border-terracotta transition-colors">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium text-charcoal-800">{q.client_name || "Unnamed client"} · {q.trip?.start} → {q.trip?.end}</div>
              <div className="text-xs text-charcoal-500">{q.client_email || "—"} · {q.source} · {new Date(q.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-right">
              <span className="inline-block rounded-full bg-sand-100 px-2.5 py-0.5 text-xs text-charcoal-600 capitalize">{q.status}</span>
              <div className="font-serif text-terracotta mt-1">{money((q.adjusted?.total ?? q.system_estimate?.total), q.system_estimate?.currency)}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function QuoteDetail({ id, onBack }) {
  const [q, setQ] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");
  const [clientEmail, setClientEmail] = useState("");
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get(`/admin/planner/quotes/${id}`).then((r) => {
      const data = r.data;
      setQ(data);
      const base = data.adjusted?.line_items || data.system_estimate.line_items;
      setItems(base.map((li) => ({ ...li })));
      setTotal(data.adjusted?.total ?? data.system_estimate.total);
      setNotes(data.admin_notes || "");
      setStatus(data.status || "draft");
      setClientEmail(data.client_email || "");
    });
  }, [id]);

  if (!q) return <p className="text-charcoal-500">Loading…</p>;
  const cur = q.system_estimate?.currency || "USD";
  const computed = items.reduce((s, li) => s + Number(li.amount || 0), 0);

  const editAmount = (i, v) => setItems((arr) => arr.map((li, j) => (j === i ? { ...li, amount: Number(v) } : li)));
  const save = async () => {
    try {
      await api.put(`/admin/planner/quotes/${id}`, {
        adjusted: { line_items: items, total: Number(total), currency: cur },
        admin_notes: notes, status, client_email: clientEmail,
      });
      toast.success("Quote updated.");
      onBack();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/planner/quotes/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Could not open the PDF."); }
  };

  const emailQuote = async () => {
    if (!clientEmail || !clientEmail.includes("@")) return toast.error("Enter a valid client email first.");
    setEmailing(true);
    try {
      const res = await api.post(`/admin/planner/quotes/${id}/email`, { email: clientEmail, origin_url: window.location.origin });
      if (res.data?.sent) toast.success("Branded quote PDF emailed to the client.");
      else if (res.data?.reason === "disabled_or_unconfigured")
        toast.error("Email isn't configured yet. Add a Resend API key to send quotes to clients.");
      else toast.error("Could not send the email. Please try again.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setEmailing(false);
  };

  return (
    <div className="space-y-5">
      <button onClick={onBack} data-testid="quote-back" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-terracotta">
        <ArrowLeft size={15} /> Back to quotes
      </button>
      <Card>
        <div className="text-sm text-charcoal-500">Route</div>
        <div className="font-medium text-charcoal-800">{q.trip?.start} → {q.trip?.end}
          {q.trip?.stops?.length ? ` (via ${q.trip.stops.join(", ")})` : ""}</div>
        <div className="text-xs text-charcoal-500 mt-1">{q.route?.distance_km} km · {q.route?.drive_hours} h · {q.route?.nights} overnight(s)</div>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-1">System estimate vs. adjusted (override any line)</h3>
        <p className="text-xs text-charcoal-500 mb-4">Both the original system estimate and your adjusted quote are saved.</p>
        <div className="space-y-2">
          {items.map((li, i) => (
            <div key={li.key} data-testid={`adj-line-${li.key}`} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="text-sm text-charcoal-800">{li.label}</div>
                {li.detail && <div className="text-xs text-charcoal-400">{li.detail}</div>}
              </div>
              <input type="number" value={li.amount} onChange={(e) => editAmount(i, e.target.value)}
                className="w-32 bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-sage/50" />
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-sand-200 pt-3 space-y-2">
          <div className="flex items-center justify-between text-sm text-charcoal-500">
            <span>Sum of line items</span><span>{money(computed, cur)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium text-charcoal-800">Final total (override)</span>
            <input data-testid="adj-total" type="number" value={total} onChange={(e) => setTotal(e.target.value)}
              className="w-36 bg-white border border-terracotta/40 rounded-lg px-3 py-2 text-sm text-right font-semibold focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
          </div>
          <button onClick={() => setTotal(computed)} className="text-xs text-terracotta hover:underline">Reset total to sum of lines</button>
        </div>
      </Card>

      <Card>
        <label className="text-sm font-medium text-charcoal-600 mb-1.5 block">Override / internal notes</label>
        <textarea data-testid="adj-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={IN} />
        <div className="mt-4 flex items-center gap-3">
          <select data-testid="adj-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-40 bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-sage/50">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <SaveButton testid="adj-save" onClick={save} children="Save quote" />
        </div>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-1">Send to client</h3>
        <p className="text-xs text-charcoal-500 mb-4">Save your changes first, then email the client a branded PDF of this quote (uses the adjusted totals).</p>
        <label className="text-sm font-medium text-charcoal-600 mb-1.5 block">Client email</label>
        <input data-testid="quote-client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
          placeholder="client@email.com" className={IN + " max-w-sm"} />
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="quote-download-pdf" onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-5 py-2.5 text-sm text-charcoal-700 font-medium hover:bg-sand-50">
            <FileDown size={15} /> Preview / download PDF
          </button>
          <button data-testid="quote-email-client" onClick={emailQuote} disabled={emailing}
            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
            <Mail size={15} /> {emailing ? "Sending…" : "Email quote to client"}
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Pricing settings ---------------- */
const NUM_FIELDS = [
  ["driver_rate_per_km", "Driver rate ($/km)"],
  ["driver_min_floor", "Driver minimum floor ($)"],
  ["toll_rate_per_km", "Toll estimate ($/km)"],
  ["exchange_rate_usd_mxn", "Exchange rate (1 USD = ? MXN)"],
  ["overnight_allowance_per_night", "Overnight allowance ($/night)"],
  ["lodging_per_night", "Lodging ($/night, separate mode)"],
  ["meals_per_day", "Meals ($/day, separate mode)"],
  ["max_daily_drive_hours", "Max daily drive hours"],
  ["avg_speed_kmh", "Average speed (km/h)"],
  ["road_factor", "Road factor (× straight line)"],
  ["safety_factor", "Safety factor (× distance)"],
];

function PricingSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/planner/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-charcoal-500">Loading…</p>;
  const setF = (k, v) => setS({ ...s, [k]: v });
  const setPet = (k, v) => setS({ ...s, pets: { ...s.pets, [k]: Number(v) } });

  const save = async () => {
    setSaving(true);
    const floor = Number(s.driver_min_floor);
    if (floor < 2000) { toast.error("Driver floor cannot be below $2,000. Resetting to $2,000."); s.driver_min_floor = 2000; }
    try { const { _id, pricing_types, ...p } = s; setS((await api.put("/admin/planner/settings", p)).data); toast.success("Pricing settings saved."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="font-medium text-charcoal-800 mb-1">Core rates</h3>
        <p className="text-xs text-charcoal-500 mb-4">Driver pay = max(distance × rate, floor). The floor can never go below $2,000.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {NUM_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-sm text-charcoal-600 mb-1 block">{label}</label>
              <input data-testid={`price-${k}`} type="number" step="0.001" value={s[k] ?? 0}
                onChange={(e) => setF(k, Number(e.target.value))} className={IN} />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-1">Deposit to accept a quote</h3>
        <p className="text-xs text-charcoal-500 mb-4">What the client pays (via Stripe) to accept a quote and secure their move.</p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="text-sm text-charcoal-600 mb-1 block">Deposit type</label>
            <select data-testid="price-deposit-type" value={s.deposit_type || "flat"} onChange={(e) => setF("deposit_type", e.target.value)} className={IN}>
              <option value="flat">Flat amount ($)</option>
              <option value="percent">Percent of total (%)</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-charcoal-600 mb-1 block">Deposit value</label>
            <input data-testid="price-deposit-value" type="number" value={s.deposit_value ?? 0} onChange={(e) => setF("deposit_value", Number(e.target.value))} className={IN} />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-4">Overnight / lodging mode</h3>
        <select data-testid="price-lodging-mode" value={s.lodging_mode} onChange={(e) => setF("lodging_mode", e.target.value)} className={IN + " max-w-xs"}>
          <option value="combined">Combined overnight allowance</option>
          <option value="separate">Separate lodging + meals</option>
        </select>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-4">Pet pricing</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-sm text-charcoal-600 mb-1 block">First pet ($)</label>
            <input data-testid="price-pet-first" type="number" value={s.pets?.first_pet_fee ?? 0} onChange={(e) => setPet("first_pet_fee", e.target.value)} className={IN} /></div>
          <div><label className="text-sm text-charcoal-600 mb-1 block">Each additional ($)</label>
            <input data-testid="price-pet-additional" type="number" value={s.pets?.additional_pet_fee ?? 0} onChange={(e) => setPet("additional_pet_fee", e.target.value)} className={IN} /></div>
          <div><label className="text-sm text-charcoal-600 mb-1 block">Special handling ($)</label>
            <input data-testid="price-pet-special" type="number" value={s.pets?.special_handling_surcharge ?? 0} onChange={(e) => setPet("special_handling_surcharge", e.target.value)} className={IN} /></div>
        </div>
      </Card>

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-4">Safe route rules (guidance for the safety-first engine)</h3>
        <div className="space-y-3">
          <div><label className="text-sm text-charcoal-600 mb-1 block">Preferred corridors</label>
            <textarea data-testid="price-corridors" rows={2} value={s.preferred_corridors || ""} onChange={(e) => setF("preferred_corridors", e.target.value)} className={IN} /></div>
          <div><label className="text-sm text-charcoal-600 mb-1 block">Avoid rules</label>
            <textarea data-testid="price-avoid" rows={2} value={s.avoid_rules || ""} onChange={(e) => setF("avoid_rules", e.target.value)} className={IN} /></div>
          <div><label className="text-sm text-charcoal-600 mb-1 block">Blocked segments</label>
            <textarea data-testid="price-blocked" rows={2} value={s.blocked_segments || ""} onChange={(e) => setF("blocked_segments", e.target.value)} className={IN} /></div>
        </div>
      </Card>

      <SaveButton testid="price-save" onClick={save} saving={saving} />
    </div>
  );
}

/* ---------------- Add-ons manager ---------------- */
const PTYPES = ["flat", "per_pet", "per_stop", "per_night", "per_hour", "percent", "manual"];

function AddonsManager({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/planner/addons").then((r) => { setRows(r.data); onChange?.(); });
  useEffect(() => { load(); }, []);
  if (!rows) return <p className="text-charcoal-500">Loading…</p>;

  const add = async () => { await api.post("/admin/planner/addons", { name: "New service", pricing_type: "flat", default_amount: 0 }); load(); };
  const upd = async (id, patch) => { await api.put(`/admin/planner/addons/${id}`, patch); };
  const del = async (id) => { await api.delete(`/admin/planner/addons/${id}`); load(); };

  return (
    <div className="space-y-3">
      {rows.map((a) => (
        <Card key={a.id}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-charcoal-500">Name</label>
              <input data-testid={`addon-name-${a.id}`} defaultValue={a.name} onBlur={(e) => upd(a.id, { name: e.target.value })} className={IN} /></div>
            <div><label className="text-xs text-charcoal-500">Description</label>
              <input defaultValue={a.description} onBlur={(e) => upd(a.id, { description: e.target.value })} className={IN} /></div>
            <div><label className="text-xs text-charcoal-500">Pricing type</label>
              <select data-testid={`addon-type-${a.id}`} defaultValue={a.pricing_type} onChange={(e) => upd(a.id, { pricing_type: e.target.value })} className={IN}>
                {PTYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div><label className="text-xs text-charcoal-500">Default amount ($ or %)</label>
              <input data-testid={`addon-amount-${a.id}`} type="number" defaultValue={a.default_amount} onBlur={(e) => upd(a.id, { default_amount: Number(e.target.value) })} className={IN} /></div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-charcoal-600">
              <input type="checkbox" defaultChecked={a.enabled} onChange={(e) => upd(a.id, { enabled: e.target.checked })} className="accent-terracotta" /> Enabled
            </label>
            <button data-testid={`addon-del-${a.id}`} onClick={() => del(a.id)} className="inline-flex items-center gap-1 text-sm text-charcoal-400 hover:text-terracotta"><Trash2 size={14} /> Delete</button>
          </div>
        </Card>
      ))}
      <button data-testid="addon-add" onClick={add} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover">
        <Plus size={15} /> Add service
      </button>
    </div>
  );
}

/* ---------------- Client links ---------------- */
function ClientLinks() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", note: "" });
  const load = () => api.get("/admin/planner/invites").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/admin/planner/invites", form); setForm({ client_name: "", client_email: "", note: "" }); load(); toast.success("Client link created."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const revoke = async (id) => { await api.delete(`/admin/planner/invites/${id}`); load(); };
  const linkFor = (t) => `${window.location.origin}/plan/${t}`;
  const copy = (t) => { navigator.clipboard.writeText(linkFor(t)); toast.success("Link copied."); };

  return (
    <div className="space-y-5">
      <Card>
        <h3 className="font-medium text-charcoal-800 mb-4">Create a private client planner link</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="invite-name" placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={IN} />
          <input data-testid="invite-email" placeholder="Client email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={IN} />
          <input data-testid="invite-note" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={IN} />
        </div>
        <button data-testid="invite-create" onClick={create} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover">
          <Link2 size={15} /> Generate link
        </button>
      </Card>
      {!rows ? <p className="text-charcoal-500">Loading…</p> : rows.map((inv) => (
        <div key={inv.id} data-testid={`invite-${inv.id}`} className={`rounded-xl border p-4 ${inv.active ? "border-sand-200 bg-white" : "border-sand-200 bg-sand-50 opacity-70"}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-medium text-charcoal-800">{inv.client_name || "Unnamed"} {inv.active ? "" : "· revoked"}</div>
              <div className="text-xs text-charcoal-500">{inv.client_email || "—"} · {inv.submissions} submission(s)</div>
              <code className="text-xs text-terracotta break-all">{linkFor(inv.token)}</code>
            </div>
            <div className="flex gap-2">
              <button data-testid={`invite-copy-${inv.id}`} onClick={() => copy(inv.token)} className="inline-flex items-center gap-1 rounded-lg border border-sand-200 px-3 py-2 text-sm text-charcoal-600 hover:bg-sand-50"><Copy size={14} /> Copy</button>
              {inv.active && <button data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="inline-flex items-center gap-1 rounded-lg border border-sand-200 px-3 py-2 text-sm text-charcoal-500 hover:text-terracotta"><Trash2 size={14} /> Revoke</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
